import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/lib/supabase';
import { Svg, Path } from 'react-native-svg';
import { MagicLinkService } from '@/services/magicLinkService';
import PrimaryButton from '@/components/PrimaryButton';
import PasswordRequirements from '@/components/PasswordRequirements';
import PageHeader from '@/components/PageHeader';

export default function ResetPasswordScreen() {
  const { t } = useLanguage();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { access_token, refresh_token, email, type } = useLocalSearchParams();

  useEffect(() => {
    // Handle both old Supabase flow (access_token, refresh_token) and new Brevo flow (email, type)
    if (access_token && refresh_token) {
      // Original Supabase flow
      supabase.auth.setSession({
        access_token: access_token as string,
        refresh_token: refresh_token as string,
      });
    } else if (email && type === 'recovery') {
      // New Brevo deep link flow - user can reset password directly
      console.log('🔄 Password reset via Brevo deep link for:', email);
    }
  }, [access_token, refresh_token, email, type]);

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      Alert.alert(t('auth.resetPassword.errorTitle'), t('auth.resetPassword.errorFillFields'));
      return;
    }

    if (newPassword !== confirmPassword) {
      Alert.alert(t('auth.resetPassword.errorTitle'), t('auth.resetPassword.errorPasswordMismatch'));
      return;
    }

    // Validate password requirements
    const validation = MagicLinkService.validatePassword(newPassword);
    if (!validation.isValid) {
      Alert.alert(
        t('auth.resetPassword.errorInvalidPassword'),
        t('auth.resetPassword.errorPasswordRequirements') + '\n\n' + 
        validation.errors.map(error => `• ${error}`).join('\n')
      );
      return;
    }

    setLoading(true);

    try {
      // Handle password reset based on the flow type
      if (email && type === 'recovery') {
        // New Brevo flow: Use our server endpoint to complete password reset
        const { env } = await import('@/config/environment');
        
        // Use configured API URL or fallback to production URL
        const apiUrl = env.apiUrl && env.apiUrl !== env.supabaseUrl 
          ? env.apiUrl 
          : 'https://mobile.padrinho.app';
        
        const response = await fetch(`${apiUrl}/api/auth/complete-password-reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email as string,
            new_password: newPassword,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to reset password');
        }

        Alert.alert(
          t('auth.resetPassword.successTitle'),
          t('auth.resetPassword.successMessage'),
          [
            {
              text: t('auth.resetPassword.successButton'),
              onPress: () => router.replace('/login'),
            }
          ]
        );
      } else {
        // Original Supabase flow
        const { error } = await supabase.auth.updateUser({
          password: newPassword,
        });

        if (error) {
          Alert.alert(t('auth.resetPassword.errorTitle'), error.message);
        } else {
          Alert.alert(
            t('auth.resetPassword.successTitle'),
            t('auth.resetPassword.successMessage'),
            [
              {
                text: t('auth.resetPassword.successButton'),
                onPress: () => router.replace('/login'),
              }
            ]
          );
        }
      }
    } catch (error) {
      Alert.alert(t('auth.resetPassword.errorTitle'), t('auth.resetPassword.errorUnexpected'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackPress = () => {
    router.back();
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader title={t('auth.resetPassword.title')} onBack={handleBackPress} />

      <KeyboardAvoidingView 
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.section}>
            <Text style={styles.title}>
              {t('auth.resetPassword.subtitle')}
            </Text>

            <View style={styles.field}>
              <Text style={styles.label}>{t('auth.resetPassword.newPassword')}</Text>
              <View style={styles.passwordFieldWrapper}>
                <TextInput
                  style={styles.input}
                  placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
                  placeholderTextColor="#A19F97"
                  color="#151515"
                  value={newPassword}
                  onChangeText={setNewPassword}
                  secureTextEntry={!showPassword}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <TouchableOpacity 
                  style={styles.eyeButton}
                  onPress={() => setShowPassword(!showPassword)}
                >
                  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <Path
                      d="M15.58 12c0 1.98-1.6 3.58-3.58 3.58S8.42 13.98 8.42 12s1.6-3.58 3.58-3.58 3.58 1.6 3.58 3.58Z"
                      stroke="#757C85"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <Path
                      d="M12 20.27c3.53 0 6.82-2.08 9.11-5.68.9-1.41.9-3.78 0-5.19C19.82 5.81 16.53 3.73 12 3.73S4.18 5.81 1.89 9.4c-.9 1.41-.9 3.78 0 5.19C4.18 18.19 7.47 20.27 12 20.27Z"
                      stroke="#757C85"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {!showPassword && (
                      <Path
                        d="m4.5 4.5 15 15"
                        stroke="#757C85"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                  </Svg>
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>{t('auth.resetPassword.confirmPassword')}</Text>
              <View style={styles.passwordFieldWrapper}>
                <TextInput
                  style={styles.input}
                  placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                  placeholderTextColor="#A19F97"
                  color="#151515"
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  secureTextEntry={!showConfirmPassword}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <TouchableOpacity 
                  style={styles.eyeButton}
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <Path
                      d="M15.58 12c0 1.98-1.6 3.58-3.58 3.58S8.42 13.98 8.42 12s1.6-3.58 3.58-3.58 3.58 1.6 3.58 3.58Z"
                      stroke="#757C85"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <Path
                      d="M12 20.27c3.53 0 6.82-2.08 9.11-5.68.9-1.41.9-3.78 0-5.19C19.82 5.81 16.53 3.73 12 3.73S4.18 5.81 1.89 9.4c-.9 1.41-.9 3.78 0 5.19C4.18 18.19 7.47 20.27 12 20.27Z"
                      stroke="#757C85"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {!showConfirmPassword && (
                      <Path
                        d="m4.5 4.5 15 15"
                        stroke="#757C85"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                  </Svg>
                </TouchableOpacity>
              </View>
            </View>

            <PasswordRequirements validation={MagicLinkService.validatePassword(newPassword)} />
          </View>
        </ScrollView>

        <View style={[styles.buttonContainer, { paddingBottom: insets.bottom + 24 }]}>
          <PrimaryButton
            title={loading ? t('auth.resetPassword.buttonLoading') : t('auth.resetPassword.button')}
            onPress={handleResetPassword}
            disabled={loading || !newPassword || !confirmPassword || !MagicLinkService.validatePassword(newPassword).isValid || newPassword !== confirmPassword}
          />
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  section: {
    padding: 24,
    alignItems: 'flex-start',
    gap: 24,
    flex: 1,
  },
  buttonContainer: {
    paddingHorizontal: 24,
    paddingBottom: 24,
    paddingTop: 16,
    backgroundColor: '#FFFFFF',
  },
  title: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 29,
  },
  field: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 12,
    flexDirection: 'column',
    width: '100%',
  },
  label: {
    color: '#151515',
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 14,
  },
  passwordFieldWrapper: {
    width: '100%',
    position: 'relative',
  },
  input: {
    width: '100%',
    height: 48,
    paddingVertical: 12,
    paddingHorizontal: 12,
    paddingRight: 48,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 14,
    color: '#151515',
    lineHeight: 20,
  },
  eyeButton: {
    position: 'absolute',
    right: 12,
    top: 12,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
});